import React, { useEffect, useState } from 'react';
import {
    collection, query, where, limit, getDocs, getDoc, doc, addDoc, orderBy,
    onSnapshot, serverTimestamp,
} from 'firebase/firestore';
import {
    X, Phone, PhoneIncoming, PhoneMissed, Clock, AlertCircle, Send, CheckCircle2, User,
} from 'lucide-react';
import { auth, db } from './firebase';
import { toLocalPhone } from './CallHistory';

/**
 * ─────────────────────────────────────────────────────────────
 *  Without Orders পেজের popup গুলো (Flutter এর WithoutOrdersScreen এর popup এর React রূপ)
 *    • CallTriesModal       — ওই নাম্বারে কল সার্ভার থেকে যতবার কল গেছে তার পুরো রেকর্ড (callRecords)
 *    • NotesModal           — withoutorders/{id}/notes
 *    • WhatsAppClicksModal  — withoutorders/{id}/whatsappclick
 *    • FraudModal           — Steadfast / Pathao / RedX fraud check
 *    • OrdersModal          — ওই নাম্বারের আগের অর্ডার (orders.user_id)
 *    • SellInfoModal        — sell হয়ে গেলে কে কনফার্ম করেছে
 * ─────────────────────────────────────────────────────────────
 */

/* ───────────────────────── shared helpers ───────────────────────── */

const pad = (n) => String(n).padStart(2, '0');

export const STATUS_STYLE = {
    'Call Ended': 'bg-green-100 text-green-700',
    'No Answer': 'bg-amber-100 text-amber-700',
    'Busy': 'bg-amber-100 text-amber-700',
    'Rejected': 'bg-red-100 text-red-700',
    'Unavailable': 'bg-red-100 text-red-700',
    'Call Failed': 'bg-red-100 text-red-700',
    'Cancelled': 'bg-gray-100 text-gray-600',
};

export const fmtDuration = (sec) => {
    const s = Math.max(0, Math.round(Number(sec) || 0));
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const r = s % 60;
    return r ? `${m}m ${r}s` : `${m}m`;
};

/** Firestore Timestamp / Date / ISO string / সংখ্যা (সেকেন্ড বা মিলিসেকেন্ড) → ms (না পারলে 0) */
export const toMs = (v) => {
    if (!v) return 0;
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (v instanceof Date) return v.getTime() || 0;
    if (typeof v === 'number') return v < 1e12 ? v * 1000 : v;
    if (typeof v === 'string') {
        const t = Date.parse(v.includes('T') ? v : v.replace(' ', 'T'));
        return Number.isNaN(t) ? 0 : t;
    }
    return 0;
};

/** ক্লায়েন্টের লোকাল সময়ে 'dd/mm/yyyy hh:mm AM' (Dart এর 'dd-MM-yyyy hh:mm a' এর মতো) */
export const fmtDateTime = (ms) => {
    if (!ms) return 'N/A';
    const d = new Date(ms);
    const h = d.getHours();
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(h12)}:${pad(d.getMinutes())} ${h >= 12 ? 'PM' : 'AM'}`;
};

/** callRecords এর একটা কলের সময় (ms) — CallHistory.js এর callMillis এর মতোই */
export const callMillis = (c) => {
    if (c.timestamp) return Number(c.timestamp) * 1000;
    const ms = c.createdAt?.toMillis?.();
    if (ms) return ms;
    return toMs(c.start_time);
};

const phoneVariants = (raw) => {
    const local = toLocalPhone(raw);
    const set = new Set([String(raw || '').trim(), local]);
    if (local) {
        set.add(`88${local}`);
        set.add(`+88${local}`);
    }
    return Array.from(set).filter(Boolean);
};

function ModalShell({ title, subtitle, onClose, children, wide }) {
    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" onClick={onClose}>
            <div
                className={`bg-white rounded-2xl p-6 w-full ${wide ? 'max-w-3xl' : 'max-w-xl'} border border-gray-200 max-h-[88vh] overflow-y-auto`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-start mb-4 gap-3">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">{title}</h2>
                        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
                    </div>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-900 flex-shrink-0"><X size={22} /></button>
                </div>
                {children}
            </div>
        </div>
    );
}

const Spinner = () => (
    <div className="flex justify-center py-10">
        <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-orange-500"></div>
    </div>
);

/* ───────────────────────── Call tries ───────────────────────── */

/**
 * ওই নাম্বারে কল সার্ভার থেকে করা সব কল (callRecords.number == 01XXXXXXXXX)।
 * createdMs এর আগের কল "আগের কল" ট্যাগ পায় — এগুলো ওই lead এর follow-up হিসেবে গোনা হয় না।
 */
export function CallTriesModal({ row, onClose }) {
    const [state, setState] = useState({ loading: true, calls: [], error: false });
    const local = toLocalPhone(row.phone);

    useEffect(() => {
        let alive = true;
        if (local.length < 10) {
            setState({ loading: false, calls: [], error: false });
            return undefined;
        }
        getDocs(query(collection(db, 'callRecords'), where('number', '==', local), limit(300)))
            .then((snap) => {
                if (!alive) return;
                const calls = snap.docs.map((d) => ({ ...d.data(), id: d.id }))
                    .sort((a, b) => callMillis(b) - callMillis(a));
                setState({ loading: false, calls, error: false });
            })
            .catch((e) => {
                console.error('callRecords read failed:', e);
                if (alive) setState({ loading: false, calls: [], error: true });
            });
        return () => { alive = false; };
    }, [local]);

    const { loading, calls, error } = state;
    const after = calls.filter((c) => callMillis(c) >= row.createdMs);
    const answered = after.filter((c) => c.answered).length;
    const talk = after.reduce((s, c) => s + (Number(c.duration) || 0), 0);

    return (
        <ModalShell title="Call History (কল সার্ভার)" onClose={onClose} wide>
            <div className="mb-4 p-4 bg-gray-50 rounded-lg text-sm text-gray-700">
                <p>Customer: <span className="font-semibold">{row.name || 'N/A'}</span></p>
                <p>Phone: <span className="font-semibold">{row.phone || 'N/A'}</span></p>
                <p>Without-order হয়েছে: <span className="font-semibold">{fmtDateTime(row.createdMs)}</span></p>
            </div>

            {error && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg flex gap-2">
                    <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                    <span>callRecords পড়া যায়নি — Firestore rules এ callRecords পড়ার অনুমতি লাগবে।</span>
                </div>
            )}

            {loading ? <Spinner /> : calls.length === 0 ? (
                <p className="text-center text-gray-500 py-10">এই নাম্বারে কল সার্ভার থেকে এখনো কোনো কল করা হয়নি</p>
            ) : (
                <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                        <div className="bg-orange-50 rounded-lg p-3 text-center">
                            <div className="text-xl font-bold text-gray-900">{after.length}</div>
                            <div className="text-xs text-gray-500">Try (এই lead এর পর)</div>
                        </div>
                        <div className="bg-green-50 rounded-lg p-3 text-center">
                            <div className="text-xl font-bold text-gray-900">{answered}</div>
                            <div className="text-xs text-gray-500">ধরেছে</div>
                        </div>
                        <div className="bg-amber-50 rounded-lg p-3 text-center">
                            <div className="text-xl font-bold text-gray-900">{after.length - answered}</div>
                            <div className="text-xs text-gray-500">ধরেনি</div>
                        </div>
                        <div className="bg-gray-50 rounded-lg p-3 text-center">
                            <div className="text-xl font-bold text-gray-900">{fmtDuration(talk)}</div>
                            <div className="text-xs text-gray-500">Talk time</div>
                        </div>
                    </div>

                    <div className="space-y-3">
                        {calls.map((c) => {
                            const Icon = c.answered ? PhoneIncoming : (c.status === 'No Answer' || c.status === 'Busy') ? PhoneMissed : Phone;
                            const isBefore = callMillis(c) < row.createdMs;
                            return (
                                <div key={c.id} className={`border border-gray-200 rounded-lg p-3 ${isBefore ? 'opacity-70' : ''}`}>
                                    <div className="flex flex-wrap items-center justify-between gap-2">
                                        <div className="flex items-center gap-2">
                                            <Icon size={16} className="text-orange-500" />
                                            <span className={`text-xs px-2 py-1 rounded ${STATUS_STYLE[c.status] || 'bg-gray-100 text-gray-600'}`}>
                                                {c.status || 'Unknown'}
                                            </span>
                                            {isBefore && (
                                                <span className="text-xs px-2 py-1 rounded bg-gray-100 text-gray-500">আগের কল</span>
                                            )}
                                        </div>
                                        <div className="text-sm text-gray-700">{c.start_time || fmtDateTime(callMillis(c))}</div>
                                    </div>
                                    <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-500">
                                        <span className="flex items-center gap-1"><Clock size={12} /> {fmtDuration(c.duration)}</span>
                                        {c.username && <span>By: {c.username}</span>}
                                        {c.answered && c.endedBy && (
                                            <span>Ended by: {c.endedBy === 'admin' ? 'Admin' : 'Customer'}</span>
                                        )}
                                        {c.failReason && <span>Reason: {c.failReason}</span>}
                                    </div>
                                    {c.recordingUrl && (
                                        <audio controls preload="none" src={c.recordingUrl} className="mt-2 w-full h-9" />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </ModalShell>
    );
}

/* ───────────────────────── Notes ───────────────────────── */

/** withoutorders/{noteDocId}/notes — Flutter এর NotesPopup এর মতোই (noteDocId = client_id) */
export function NotesModal({ noteDocId, name, onClose }) {
    const [notes, setNotes] = useState(null);
    const [text, setText] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const q = query(collection(db, 'withoutorders', noteDocId, 'notes'), orderBy('timestamp', 'desc'));
        const unsub = onSnapshot(q, (snap) => {
            setNotes(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        }, (e) => {
            console.error('notes read failed:', e);
            setError('নোট পড়া যায়নি (Firestore rules দেখুন)');
            setNotes([]);
        });
        return () => unsub();
    }, [noteDocId]);

    const save = async () => {
        const t = text.trim();
        if (!t) return;
        setSaving(true);
        setError('');
        try {
            await addDoc(collection(db, 'withoutorders', noteDocId, 'notes'), {
                text: t,
                createdBy: auth.currentUser?.email || '',
                timestamp: serverTimestamp(),
            });
            setText('');
        } catch (e) {
            console.error(e);
            setError(`সেভ করা যায়নি: ${e.message}`);
        }
        setSaving(false);
    };

    return (
        <ModalShell title="নোটস" subtitle={name} onClose={onClose}>
            <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={3}
                placeholder="নোট লিখুন..."
                className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
            <button
                onClick={save}
                disabled={saving || !text.trim()}
                className="mt-2 w-full flex items-center justify-center gap-2 bg-orange-400 text-white hover:bg-orange-500 disabled:opacity-50 px-4 py-2.5 rounded-lg transition font-semibold text-sm"
            >
                <Send size={15} /> সেভ করুন
            </button>
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}

            <div className="mt-5 pt-4 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-800 mb-3">সব নোটস</h3>
                {notes === null ? <Spinner /> : notes.length === 0 ? (
                    <p className="text-center text-gray-500 py-6 text-sm">কোন নোট নেই</p>
                ) : (
                    <div className="space-y-3">
                        {notes.map((n) => (
                            <div key={n.id} className="border border-gray-200 rounded-lg p-3">
                                <p className="text-sm text-gray-900 whitespace-pre-wrap">{n.text}</p>
                                <p className="text-xs text-green-700 mt-1">{n.createdBy}</p>
                                <p className="text-xs text-gray-500 mt-0.5">{n.timestamp ? fmtDateTime(toMs(n.timestamp)) : 'সময় নেই'}</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </ModalShell>
    );
}

/* ───────────────────────── WhatsApp clicks ───────────────────────── */

export function WhatsAppClicksModal({ docId, onClose }) {
    const [state, setState] = useState({ loading: true, clicks: [], error: false });

    useEffect(() => {
        let alive = true;
        getDocs(query(collection(db, 'withoutorders', docId, 'whatsappclick'), orderBy('clickedAt', 'desc')))
            .then((snap) => {
                if (alive) setState({ loading: false, clicks: snap.docs.map((d) => ({ id: d.id, ...d.data() })), error: false });
            })
            .catch((e) => {
                console.error('whatsappclick read failed:', e);
                if (alive) setState({ loading: false, clicks: [], error: true });
            });
        return () => { alive = false; };
    }, [docId]);

    const { loading, clicks, error } = state;
    return (
        <ModalShell title="WhatsApp Clicks" onClose={onClose}>
            {loading ? <Spinner /> : error ? (
                <p className="text-center text-red-600 py-6 text-sm">Clicks পড়া যায়নি</p>
            ) : clicks.length === 0 ? (
                <p className="text-center text-gray-500 py-6 text-sm">No clicks recorded yet</p>
            ) : (
                <div className="space-y-2">
                    {clicks.map((c, i) => (
                        <div key={c.id} className="flex items-center gap-3 border border-gray-200 rounded-lg p-3">
                            <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center text-sm font-semibold flex-shrink-0">{i + 1}</div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-gray-900 break-all">{c.clickedBy || 'Unknown'}</div>
                                <div className="text-xs text-gray-500">{c.clickedAt ? fmtDateTime(toMs(c.clickedAt)) : 'N/A'}</div>
                            </div>
                            <CheckCircle2 size={18} className="text-green-500 flex-shrink-0" />
                        </div>
                    ))}
                </div>
            )}
        </ModalShell>
    );
}

/* ───────────────────────── Fraud check ───────────────────────── */

const FRAUD_APIS = {
    steadfast: (p) => `https://steadfast-fraud-check.sparkx-ai.shop/check-fraud/${p}`,
    pathao: (p) => `https://pathao-fraud-check.sparkx-ai.shop/api/fraud-check?phone=${p}`,
    redx: (p) => `https://redx-fraud-check.sparkx-ai.shop/api/redx-fraud-check?phone=${p}`,
};

async function callFraudApi(url, label) {
    try {
        const res = await fetch(url);
        if (res.ok) return { data: await res.json(), error: null };
        let msg = `${label} API কল ব্যর্থ: ${res.status}`;
        try {
            const body = await res.json();
            if (body && body.message) msg += ` (${body.message})`;
        } catch (e) { /* body JSON না হলে বাদ */ }
        return { data: null, error: msg };
    } catch (e) {
        return { data: null, error: `${label} এ একটি ত্রুটি ঘটেছে: ${e.message}` };
    }
}

function FraudCard({ title, color, result }) {
    return (
        <div className="border border-gray-200 rounded-xl p-4 shadow-sm">
            <h3 className={`text-base font-bold mb-2 ${color}`}>{title}</h3>
            <div className="border-t border-gray-100 pt-3 text-sm text-gray-800 space-y-1">
                {result === undefined ? <Spinner /> : result.error ? (
                    <p className="text-red-600">{result.error}</p>
                ) : result.body}
            </div>
        </div>
    );
}

export function FraudModal({ phone, onClose }) {
    const [res, setRes] = useState({});

    useEffect(() => {
        let alive = true;
        const set = (k, v) => { if (alive) setRes((r) => ({ ...r, [k]: v })); };
        callFraudApi(FRAUD_APIS.steadfast(phone), 'স্টেডফাস্ট').then((r) => set('steadfast', r));
        callFraudApi(FRAUD_APIS.pathao(phone), 'পাঠাও').then((r) => set('pathao', r));
        callFraudApi(FRAUD_APIS.redx(phone), 'RedX').then((r) => set('redx', r));
        return () => { alive = false; };
    }, [phone]);

    const buildSteadfast = (r) => {
        if (!r || r.error) return r;
        const d = r.data || {};
        const frauds = Array.isArray(d.frauds) ? d.frauds : [];
        return {
            error: null,
            body: (
                <>
                    <p>মোট ডেলিভারি: {d.total_delivered ?? 'N/A'}</p>
                    <p>মোট বাতিল: {d.total_cancelled ?? 'N/A'}</p>
                    <p className="font-semibold pt-2">ফ্রড তালিকা:</p>
                    {frauds.length === 0 ? (
                        <p className="italic text-gray-500">কোনো ফ্রড ডেটা পাওয়া যায়নি।</p>
                    ) : frauds.map((f, i) => (
                        <div key={i} className="bg-red-50 border border-red-200 rounded-lg p-3 mb-2">
                            <p className="font-semibold">নাম: {f.name ?? 'N/A'}</p>
                            <p>ফোন: {f.phone ?? 'N/A'}</p>
                            <p>বিস্তারিত: {f.details ?? 'N/A'}</p>
                            <p>স্ট্যাটাস: {f.status === 1 ? 'ফ্রড' : 'সাধারণ'}</p>
                            <p>তৈরির তারিখ: {f.created_at ? String(f.created_at).split('T')[0] : 'N/A'}</p>
                        </div>
                    ))}
                </>
            ),
        };
    };

    const buildPathao = (r) => {
        if (!r || r.error) return r;
        const s = r.data?.stats;
        if (!s) return { error: 'পাঠাও ডেটা লোড করা যায়নি।' };
        return {
            error: null,
            body: (
                <>
                    <p>সফল ডেলিভারি: {s.Delivered ?? 'N/A'}</p>
                    <p>মোট ডেলিভারি: {s.Processed ?? 'N/A'}</p>
                    <p>ফ্রড কাউন্ট: {s.Returned ?? 'N/A'}</p>
                </>
            ),
        };
    };

    const buildRedx = (r) => {
        if (!r || r.error) return r;
        const d = r.data?.data;
        if (!d) return { error: 'রেডএক্স ডেটা লোড করা যায়নি।' };
        return {
            error: null,
            body: (
                <>
                    <p>মোট পার্সেল: {d.totalParcels ?? 'N/A'}</p>
                    <p>ডেলিভারি হওয়া পার্সেল: {d.deliveredParcels ?? 'N/A'}</p>
                    <p>রিটার্ন পার্সেন্টেজ: {d.returnPercentage ?? 'N/A'}%</p>
                    <p>কাস্টমার সেগমেন্ট: {d.customerSegment ?? 'N/A'}</p>
                </>
            ),
        };
    };

    return (
        <ModalShell title="ফ্রড চেক ফলাফল" subtitle={phone} onClose={onClose}>
            <div className="space-y-4">
                <FraudCard title="স্টেডফাস্ট (Steadfast) ফলাফল" color="text-purple-700" result={buildSteadfast(res.steadfast)} />
                <FraudCard title="পাঠাও (Pathao) ফলাফল" color="text-teal-700" result={buildPathao(res.pathao)} />
                <FraudCard title="রেডএক্স (RedX) ফলাফল" color="text-red-600" result={buildRedx(res.redx)} />
            </div>
        </ModalShell>
    );
}

/* ───────────────────────── Previous orders (Elevate) ───────────────────────── */

const ORDER_STATUS_CLS = {
    Delivered: 'bg-green-100 text-green-700',
    Return: 'bg-purple-100 text-purple-700',
    Cancel: 'bg-red-100 text-red-700',
    Processing: 'bg-blue-100 text-blue-700',
};

export function OrdersModal({ phone, onClose }) {
    const [state, setState] = useState({ loading: true, orders: [], error: false });

    useEffect(() => {
        let alive = true;
        const variants = phoneVariants(phone);
        if (variants.length === 0) {
            setState({ loading: false, orders: [], error: false });
            return undefined;
        }
        getDocs(query(collection(db, 'orders'), where('user_id', 'in', variants)))
            .then((snap) => {
                if (!alive) return;
                const orders = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
                    .sort((a, b) => toMs(b.created_at) - toMs(a.created_at));
                setState({ loading: false, orders, error: false });
            })
            .catch((e) => {
                console.error('orders read failed:', e);
                if (alive) setState({ loading: false, orders: [], error: true });
            });
        return () => { alive = false; };
    }, [phone]);

    const { loading, orders, error } = state;
    return (
        <ModalShell title="Order History" subtitle={phone} onClose={onClose} wide>
            {loading ? <Spinner /> : error ? (
                <p className="text-center text-red-600 py-6 text-sm">Error loading orders</p>
            ) : orders.length === 0 ? (
                <p className="text-center text-gray-500 py-10 text-sm">এই নাম্বারে কোনো অর্ডার নেই</p>
            ) : (
                <div className="space-y-4">
                    {orders.map((o) => (
                        <div key={o.id} className="border border-gray-200 rounded-xl p-4">
                            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                <span className="font-mono text-sm text-orange-600 break-all">{o.invoiceId || o.orderId || o.id}</span>
                                <span className={`text-xs px-2 py-1 rounded ${ORDER_STATUS_CLS[o.status] || 'bg-gray-100 text-gray-600'}`}>{o.status || 'Unknown'}</span>
                            </div>
                            <p className="text-xs text-gray-500 mb-2">{o.orderDate} {o.orderTime}</p>
                            <div className="text-sm text-gray-700 mb-2">
                                <p className="flex items-center gap-1.5"><User size={13} className="text-gray-400" /> {o.customerInfo?.name || 'N/A'} · {o.customerInfo?.phone || 'N/A'}</p>
                                <p className="text-xs text-gray-500 mt-0.5">{o.customerInfo?.address || 'N/A'}</p>
                                {o.district && <p className="text-xs text-gray-500">{o.district}{o.division ? `, ${o.division}` : ''}</p>}
                            </div>
                            <div className="space-y-2 mb-3">
                                {(o.cartItems || []).map((it, i) => (
                                    <div key={i} className="flex items-center gap-3">
                                        {it.image && <img src={it.image} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
                                        <div className="flex-1 min-w-0 text-sm text-gray-900">{it.name || 'N/A'}</div>
                                        <div className="text-xs text-gray-500 whitespace-nowrap">৳{it.price} × {it.quantity}</div>
                                        <div className="text-sm font-semibold whitespace-nowrap">৳{(Number(it.price) || 0) * (Number(it.quantity) || 1)}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="border-t border-gray-100 pt-2 text-sm space-y-0.5">
                                <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>৳{o.subtotalAmount ?? 0}</span></div>
                                <div className="flex justify-between"><span className="text-gray-500">Delivery Charge</span><span>৳{o.CustomerDeliveryCharge ?? 0}</span></div>
                                {Number(o.discountAmount) > 0 && (
                                    <div className="flex justify-between"><span className="text-gray-500">Discount</span><span className="text-green-700">-৳{o.discountAmount}</span></div>
                                )}
                                <div className="flex justify-between font-bold"><span>Total Amount</span><span>৳{o.totalAmount ?? 0}</span></div>
                                <div className="flex justify-between text-xs text-gray-500 pt-1">
                                    <span>Payment</span>
                                    <span>{o.paymentMethod === 'cashOnDelivery' ? 'Cash on Delivery' : (o.paymentMethod || 'N/A')}</span>
                                </div>
                                {o.courier && (
                                    <div className="text-xs text-gray-500">
                                        Courier: {String(o.courier).toUpperCase()}
                                        {o.steadfastTrackingCode ? ` · Tracking: ${o.steadfastTrackingCode}` : ''}
                                        {o.steadfastStatus ? ` · ${o.steadfastStatus}` : ''}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </ModalShell>
    );
}

/* ───────────────────────── Sell confirmation info ───────────────────────── */

export function SellInfoModal({ docId, row, onOpenCalls, onClose }) {
    const [state, setState] = useState({ loading: true, data: null });

    useEffect(() => {
        let alive = true;
        getDoc(doc(db, 'withoutorders', docId))
            .then((s) => { if (alive) setState({ loading: false, data: s.exists() ? s.data() : null }); })
            .catch((e) => {
                console.error(e);
                if (alive) setState({ loading: false, data: null });
            });
        return () => { alive = false; };
    }, [docId]);

    const { loading, data } = state;
    // পুরনো কোডে ফিল্ডের নাম 'confiremedAt' (বানান ভুল) ছিল, তাই দুটোই দেখা হয়
    const confirmedMs = toMs(data?.confirmedAt) || toMs(data?.confiremedAt);

    return (
        <ModalShell title="Confirmation Info" subtitle={docId} onClose={onClose}>
            {loading ? <Spinner /> : !data ? (
                <p className="text-sm text-gray-500">No data found for this order.</p>
            ) : (
                <div className="space-y-2 text-sm text-gray-800">
                    {data.sell === true && <p className="flex items-center gap-2"><CheckCircle2 size={16} className="text-green-600" /> Sell: True</p>}
                    {data.confirmedBy && <p>Confirmed By: <span className="font-semibold break-all">{data.confirmedBy}</span></p>}
                    {confirmedMs > 0 && <p>Confirmed At: <span className="font-semibold">{fmtDateTime(confirmedMs)}</span></p>}
                    {data.sell !== true && !data.confirmedBy && !confirmedMs && (
                        <p className="text-gray-500">No related confirmation data found.</p>
                    )}
                </div>
            )}
            <button
                onClick={() => { onClose(); onOpenCalls(row); }}
                className="mt-4 w-full flex items-center justify-center gap-2 border-2 border-blue-600 bg-blue-50 text-blue-800 hover:bg-blue-100 px-4 py-2.5 rounded-lg transition font-semibold text-sm"
            >
                <Phone size={16} /> Call Details ({row.phone})
            </button>
        </ModalShell>
    );
}
